import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Award, Loader2, ShieldCheck, Plane, IdCard } from "lucide-react";

/**
 * /welcome/:token — public landing for legacy customers coming in from
 * the migration email. Renders a personalized welcome, shows the founding
 * perks they're claiming, and lets them set a password to activate.
 */
export default function LegacyWelcome() {
  const { token } = useParams();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let ok = true;
    api.get(`/auth/claim-legacy/${token}`)
      .then((r) => {
        if (!ok) return;
        setInvite(r.data);
        setFirstName(r.data.first_name || "");
      })
      .catch((e) => {
        if (!ok) return;
        setError(e?.response?.data?.detail || "Invalid or expired invite link");
      });
    return () => { ok = false; };
  }, [token]);

  const claim = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== password2) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/auth/claim-legacy/${token}`, {
        password,
        first_name: firstName || undefined,
        last_name: lastName || undefined,
      });
      // Sign the user in and land them on Cockpit
      try {
        localStorage.setItem("pjp_token", r.data.token);
        setUser && setUser(r.data.user);
      } catch { /* fallthrough */ }
      toast.success("Welcome to PJP, Founding Member 🎖️");
      nav("/dashboard?welcome=founder", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create your account");
    } finally { setBusy(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-obsidian text-white flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="mono text-[10px] tracking-widest uppercase text-red-400">// INVITE PROBLEM</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Hmm — this link isn't valid.</h1>
          <p className="text-white/60 mt-3">{error}</p>
          <p className="text-white/60 mt-3 text-sm">If you already claimed your account, just <a href="/login" className="text-cyan hover:underline">sign in</a>. If not, drop us a note at hello@privatejetpilots.com.</p>
        </div>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="min-h-screen bg-obsidian text-white flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-cyan" />
      </div>
    );
  }

  const foundingSince = invite.founding_since
    ? new Date(invite.founding_since).toLocaleDateString("en-US", { month: "long", year: "numeric" })
    : null;

  return (
    <div className="min-h-screen bg-obsidian text-white">
      <div className="max-w-md mx-auto px-4 py-12">
        {/* Founding Member ribbon */}
        <div className="rounded-full bg-cyan/[0.12] border border-cyan/40 text-cyan mono text-[10px] tracking-widest uppercase px-3 py-1.5 inline-flex items-center gap-1.5 mb-4" data-testid="founding-ribbon">
          <Award className="h-3 w-3" /> FOUNDING MEMBER · CLAIM YOUR SPOT
        </div>

        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight leading-tight">
          Hey {invite.first_name || "Pilot"} —<br/>welcome to the new PJP.
        </h1>
        <p className="text-white/70 mt-3">
          You've been part of PrivateJetPilots.com{foundingSince ? ` since ${foundingSince}` : ""}. Because you were here from the start, we saved something for you.
        </p>

        {/* The perk */}
        <div className="mt-6 rounded-lg border border-cyan/40 bg-cyan/[0.06] p-4" data-testid="founding-perk">
          <div className="flex items-center gap-2 mb-2">
            <IdCard className="h-5 w-5 text-cyan" />
            <div className="mono text-[10px] tracking-widest uppercase text-cyan">Your Founding Perk</div>
          </div>
          <div className="text-white font-semibold text-lg">PJP Digital ID — free for 12 months</div>
          <div className="text-white/70 text-sm mt-1">
            A physical NFC card + digital wallet ID that proves you're a real pilot to any FBO in the world. Normally $199/year. Yours free for 12 months the moment you claim.
          </div>
        </div>

        {/* Claim form */}
        <form onSubmit={claim} className="mt-8 space-y-4" data-testid="claim-form">
          <div>
            <label className="mono text-[10px] tracking-widest uppercase text-white/60">Email</label>
            <div className="mt-1 bg-black/40 border border-white/10 rounded px-3 py-2.5 text-white/80 font-mono text-sm">{invite.email}</div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mono text-[10px] tracking-widest uppercase text-white/60">First name</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                data-testid="input-first-name"
                className="mt-1 w-full bg-black/40 border border-white/15 rounded px-3 py-2.5 text-white outline-none focus:border-cyan/50"
                required
              />
            </div>
            <div>
              <label className="mono text-[10px] tracking-widest uppercase text-white/60">Last name</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                data-testid="input-last-name"
                className="mt-1 w-full bg-black/40 border border-white/15 rounded px-3 py-2.5 text-white outline-none focus:border-cyan/50"
                required
              />
            </div>
          </div>

          <div>
            <label className="mono text-[10px] tracking-widest uppercase text-white/60">Choose a password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="input-password"
              placeholder="At least 8 characters"
              className="mt-1 w-full bg-black/40 border border-white/15 rounded px-3 py-2.5 text-white outline-none focus:border-cyan/50"
              required
              minLength={8}
            />
          </div>
          <div>
            <label className="mono text-[10px] tracking-widest uppercase text-white/60">Confirm password</label>
            <input
              type="password"
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
              data-testid="input-password-confirm"
              className="mt-1 w-full bg-black/40 border border-white/15 rounded px-3 py-2.5 text-white outline-none focus:border-cyan/50"
              required
              minLength={8}
            />
          </div>

          <button
            type="submit"
            disabled={busy}
            data-testid="btn-claim-account"
            className="w-full mono text-[11px] tracking-widest uppercase px-4 py-3 rounded bg-cyan text-obsidian font-bold hover:brightness-110 disabled:opacity-40 inline-flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            {busy ? "Claiming…" : "Claim my Founding Member spot"}
          </button>

          <p className="text-[11px] text-white/45 leading-relaxed">
            By claiming, you agree to the PJP <a href="/terms" className="text-cyan/80 hover:underline">Terms</a> and <a href="/privacy" className="text-cyan/80 hover:underline">Privacy Policy</a>. After claim, you'll be asked to verify your identity — passport or driver's license — before you can use the community features.
          </p>
        </form>
      </div>
    </div>
  );
}
